import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { menuPermissionsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { UserAvatar } from './UserAvatar';
import { AvatarUploadDialog } from './AvatarUploadDialog';
import {
  LayoutDashboard,
  Target,
  Calendar,
  ClipboardList,
  Users,
  Settings,
  LogOut,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  Menu,
  UserCog,
  UsersRound,
  HelpCircle,
  BarChart3,
  Home,
  Cog,
  Mail,
  Network,
  Briefcase,
  ShieldCheck,
  Wrench,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { cn } from '../lib/utils';

// Storage keys
const SIDEBAR_STATE_KEY = 'sidebar_expansion_state';
const SIDEBAR_COLLAPSED_KEY = 'sidebar_collapsed';
const MENU_PERMISSIONS_CACHE_KEY = 'menu_permissions_cache';

// Animated Submenu Component for smooth expand/collapse
// Uses CSS grid trick for height animation without JavaScript height calculation
// Animation triggers when isOpen changes (menu open/close), NOT on children updates
const AnimatedSubmenu = ({ isOpen, children }) => {
  // Skip animation on initial render, then enable transitions via state re-render
  const [transitionEnabled, setTransitionEnabled] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setTransitionEnabled(true), 50);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: isOpen ? '1fr' : '0fr',
        opacity: isOpen ? 1 : 0,
        transition: transitionEnabled
          ? 'grid-template-rows 0.25s ease-out, opacity 0.2s ease-out'
          : 'none',
      }}
    >
      <div style={{ overflow: 'hidden' }}>
        <div className="ml-4 mt-1 space-y-1">
          {children}
        </div>
      </div>
    </div>
  );
};

// Get initial expansion state from localStorage (runs once)
const getInitialExpansionState = () => {
  try {
    const saved = localStorage.getItem(SIDEBAR_STATE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Ensure only one menu is open (accordion behavior)
      if (typeof parsed === 'string') {
        return parsed; // New format: single string key
      }
      // Migrate old format: find first open menu
      const openMenu = Object.entries(parsed).find(([_, isOpen]) => isOpen);
      return openMenu ? openMenu[0] : null;
    }
  } catch (e) {}
  return 'kpis'; // Default: KPIs menu open
};

// Get cached permissions (runs once)
const getCachedPermissions = () => {
  try {
    const cached = localStorage.getItem(MENU_PERMISSIONS_CACHE_KEY);
    if (cached) return JSON.parse(cached);
  } catch (e) {}
  return [];
};

// Static initial values (computed once at module load)
const INITIAL_EXPANSION = getInitialExpansionState();
const INITIAL_PERMISSIONS = getCachedPermissions();

const Layout = ({ children }) => {
  const { user, logout, isAdmin, updateUser } = useAuth();
  const { canAccess, canAccessModule } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Avatar dialog state
  const [showAvatarDialog, setShowAvatarDialog] = useState(false);
  
  // Mobile state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Desktop sidebar collapse state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'; } catch { return false; }
  });

  const toggleSidebarCollapse = useCallback(() => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next)); } catch {}
      return next;
    });
  }, []);
  
  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);
  
  // Expansion state - single menu key (accordion: only one open at a time)
  // Value is either a string (menu key) or null (all collapsed)
  const [openMenu, setOpenMenu] = useState(INITIAL_EXPANSION);
  
  // Permissions - initialized from static cache
  const [menuPermissions, setMenuPermissions] = useState(INITIAL_PERMISSIONS);
  const [permissionsReady, setPermissionsReady] = useState(INITIAL_PERMISSIONS.length > 0);
  
  // Refs to track initialization
  const permissionsLoadedRef = useRef(INITIAL_PERMISSIONS.length > 0);
  const userIdRef = useRef(user?.id);

  // Load permissions ONLY when user changes (login/logout)
  useEffect(() => {
    // Skip if same user
    if (userIdRef.current === user?.id && permissionsLoadedRef.current) {
      return;
    }
    userIdRef.current = user?.id;
    
    if (!user) {
      setMenuPermissions([]);
      permissionsLoadedRef.current = false;
      return;
    }

    const loadPermissions = async () => {
      try {
        const response = await menuPermissionsAPI.getAll();
        const newPermissions = response.data.permissions || [];
        
        // Only update if different
        const newJson = JSON.stringify(newPermissions);
        const currentJson = JSON.stringify(menuPermissions);
        
        if (newJson !== currentJson) {
          setMenuPermissions(newPermissions);
          try {
            localStorage.setItem(MENU_PERMISSIONS_CACHE_KEY, newJson);
          } catch (e) {}
        }
        permissionsLoadedRef.current = true;
        setPermissionsReady(true);
      } catch (error) {
        console.error('Error loading menu permissions:', error);
        permissionsLoadedRef.current = true;
        setPermissionsReady(true);
      }
    };
    
    loadPermissions();
  }, [user?.id]); // Only depend on user.id, not the entire user object

  // Toggle menu - accordion behavior (only one open at a time)
  const toggleMenu = useCallback((menuKey) => {
    setOpenMenu(prev => {
      // If clicking the same menu that's open, close it (null)
      // Otherwise, open the clicked menu (close others automatically)
      const newValue = prev === menuKey ? null : menuKey;
      // Persist to localStorage
      localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify(newValue));
      return newValue;
    });
  }, []);

  // User role - memoized
  const userRole = useMemo(() => user?.role || 'user', [user?.role]);

  // Granular permission filter (only applies to user/viewer — admins always pass)
  // Maps Layout subKey -> granular submenu key
  const KPI_SUB_MAP = { dashboard: 'dashboard', kpis_list: 'kpis', capture: 'captura', calendar: 'calendario' };
  const HELPDESK_SUB_MAP = {
    helpdesk_dashboard: 'dashboard',
    helpdesk_categories: 'categorias',
    helpdesk_reports: 'informes',
    helpdesk_email_logs: 'logs_email',
    helpdesk_settings: 'configuracion',
  };
  // Check menu visibility - stable callback with minimal dependencies
  const isMenuVisible = useCallback((menuKey, subKey = null) => {
    if (userRole === 'admin') return true;
    if (!permissionsReady || menuPermissions.length === 0) {
      // Before permissions load, only show top-level non-helpdesk items
      if (menuKey === 'helpdesk') return false;
      if (!subKey) return true;
      // Show basic KPI sub-items by default
      return menuKey === 'kpis';
    }
    
    const permission = menuPermissions.find(p => 
      p.menu_key === menuKey && (subKey ? p.sub_key === subKey : !p.sub_key)
    );
    
    if (!permission) {
      if (subKey) {
        return menuPermissions.some(p => 
          p.menu_key === menuKey && !p.is_beta &&
          (userRole === 'viewer' ? p.visible_for_viewer : p.visible_for_user)
        );
      }
      return true;
    }
    
    if (permission.is_beta) return false;
    return userRole === 'viewer' ? permission.visible_for_viewer !== false : permission.visible_for_user !== false;
  }, [menuPermissions, userRole, permissionsReady]);

  // Check if module is beta
  const isModuleBeta = useCallback((menuKey) => {
    return menuPermissions.some(p => p.menu_key === menuKey && p.is_beta);
  }, [menuPermissions]);

  // Effective visibility for a submenu: for non-admins with a granular mapping,
  // granular permissions take precedence over the legacy beta check. Admins use
  // the legacy check (granular does not apply to admins).
  const effectiveSubmenuVisible = useCallback((betaModule, betaSub, granularMod, layoutSubKey) => {
    if (userRole === 'admin') return isMenuVisible(betaModule, betaSub);
    const map = granularMod === 'kpis' ? KPI_SUB_MAP : HELPDESK_SUB_MAP;
    const granSub = map[layoutSubKey];
    if (granSub) {
      // Granular is the source of truth for this sub when mapping exists
      return canAccess(granularMod, granSub);
    }
    return isMenuVisible(betaModule, betaSub);
  }, [userRole, isMenuVisible, canAccess]);

  const granularAllowsTopLevel = useCallback((module) => {
    if (userRole === 'admin') return true;
    return canAccessModule(module);
  }, [userRole, canAccessModule]);


  // Sync openMenu with current route on every navigation
  // This ensures only the menu matching the active section is open
  useEffect(() => {
    const p = location.pathname;
    let targetMenu = null;
    if (p === '/dashboard' || p === '/kpis' || p === '/capture' || p === '/calendar' ||
        p.startsWith('/dashboard/') || p.startsWith('/kpis/') ||
        p.startsWith('/capture/') || p.startsWith('/calendar/')) {
      targetMenu = 'kpis';
    } else if (p.startsWith('/helpdesk')) {
      targetMenu = 'helpdesk';
    } else if (p.startsWith('/capital-humano')) {
      targetMenu = 'capital_humano';
    } else if (p.startsWith('/vigilancia')) {
      targetMenu = 'vigilancia';
    } else if (p.startsWith('/users')) {
      targetMenu = 'users';
    } else if (p === '/settings' || p.startsWith('/admin/menus')) {
      targetMenu = 'settings';
    }
    // else: null (for /inicio, /organigrama, etc.)
    setOpenMenu(prev => {
      if (prev === targetMenu) return prev;
      localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify(targetMenu));
      return targetMenu;
    });
  }, [location.pathname]);

  // Close mobile menu on navigation
  useEffect(() => {
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Logout handler
  const handleLogout = useCallback(() => {
    try {
      localStorage.removeItem(SIDEBAR_STATE_KEY);
      localStorage.removeItem(MENU_PERMISSIONS_CACHE_KEY);
    } catch (e) {}
    logout();
    navigate('/login');
  }, [logout, navigate]);

  // Memoized menu items - only recalculate when permissions change
  const kpisSubItems = useMemo(() => [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, subKey: 'dashboard' },
    { path: '/kpis', label: 'KPIs', icon: Target, subKey: 'kpis_list' },
    { path: '/capture', label: 'Captura', icon: ClipboardList, subKey: 'capture' },
    { path: '/calendar', label: 'Calendario', icon: Calendar, subKey: 'calendar' },
  ].filter(item => effectiveSubmenuVisible('kpis', item.subKey, 'kpis', item.subKey)), [effectiveSubmenuVisible]);

  const usersSubItems = useMemo(() => [
    { path: '/users', label: 'Usuarios', icon: UserCog, exact: true, subKey: 'users' },
    { path: '/users/teams', label: 'Equipos', icon: UsersRound, subKey: 'teams' },
  ].filter(item => isMenuVisible('admin', item.subKey)), [isMenuVisible]);

  const settingsSubItems = useMemo(() => [
    { path: '/settings', label: 'Metas y KPIs', icon: Settings, subKey: 'settings_main' },
    { path: '/admin/menus', label: 'Gestión de Menús', icon: Cog, subKey: 'menu_permissions' },
  ].filter(item => isMenuVisible('settings', item.subKey)), [isMenuVisible]);

  // Helpdesk sub-items filtered by permission
  const helpdeskSubItems = useMemo(() => [
    { path: '/helpdesk', label: 'Dashboard', icon: LayoutDashboard, subKey: 'helpdesk_dashboard', exact: true },
    { path: '/helpdesk/categories', label: 'Categorías', icon: Target, subKey: 'helpdesk_categories' },
    { path: '/helpdesk/reports', label: 'Informes', icon: BarChart3, subKey: 'helpdesk_reports' },
    { path: '/helpdesk/email-logs', label: 'Logs de Email', icon: Mail, subKey: 'helpdesk_email_logs' },
    { path: '/helpdesk/settings', label: 'Configuración', icon: Settings, subKey: 'helpdesk_settings' },
  ].filter(item => effectiveSubmenuVisible('helpdesk', item.subKey, 'mesa_de_ayuda', item.subKey)), [effectiveSubmenuVisible]);

  // Capital Humano sub-items filtered by granular permission
  const capitalHumanoSubItems = useMemo(() => [
    { path: '/capital-humano/organigrama', label: 'Organigrama', icon: Network, sub: 'organigrama' },
    { path: '/capital-humano/colaboradores', label: 'Colaboradores', icon: UsersRound, sub: 'colaboradores' },
    { path: '/capital-humano/vacantes', label: 'Vacantes', icon: Briefcase, sub: 'vacantes' },
  ].filter(item => userRole === 'admin' || canAccess('capital_humano', item.sub)), [userRole, canAccess]);

  // Vigilancia sub-items filtered by granular permission
  const vigilanciaSubItems = useMemo(() => [
    { path: '/vigilancia/proveedores-visitas', label: 'Proveedores / Visitas', icon: UsersRound, sub: 'proveedores_visitas' },
    { path: '/vigilancia/herramentales', label: 'Herramentales', icon: Wrench, sub: 'herramentales' },
    { path: '/vigilancia/personal', label: 'Personal', icon: Users, sub: 'personal' },
  ].filter(item => userRole === 'admin' || canAccess('vigilancia', item.sub)), [userRole, canAccess]);

  // Derived visibility flags
  const showHelpDesk = useMemo(() => {
    return helpdeskSubItems.length > 0;
  }, [helpdeskSubItems.length]);

  const showAdminSection = useMemo(() => usersSubItems.length > 0, [usersSubItems.length]);
  const showSettingsSection = useMemo(() => settingsSubItems.length > 0, [settingsSubItems.length]);

  // Current path for active state - derived, not stored
  const currentPath = location.pathname;

  // Path checking - pure functions, no state
  const isInKPIsSection = currentPath === '/dashboard' || currentPath === '/kpis' || 
    currentPath === '/capture' || currentPath === '/calendar' ||
    currentPath.startsWith('/dashboard/') || currentPath.startsWith('/kpis/') ||
    currentPath.startsWith('/capture/') || currentPath.startsWith('/calendar/');
  
  const isInUsersSection = currentPath.startsWith('/users');
  const isInSettingsSection = currentPath === '/settings' || currentPath.startsWith('/admin/menus');
  const isInHelpdeskSection = currentPath.startsWith('/helpdesk');
  const isInCapitalHumanoSection = currentPath.startsWith('/capital-humano');
  const isInVigilanciaSection = currentPath.startsWith('/vigilancia');

  // Render sidebar navigation content
  const renderNavContent = (onItemClick, { isMobile = false, onCollapse = () => {} } = {}) => (
    <>
      {/* Logo */}
      <div className="sidebar-logo">
        <Link to="/inicio" className="flex items-center gap-3 flex-1 min-w-0" onClick={onItemClick}>
          <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-white font-bold text-lg font-['Manrope'] truncate">Central Aretina</h1>
            <p className="text-slate-400 text-xs truncate">Plataforma de gestión</p>
          </div>
        </Link>
        {!isMobile && (
          <button
            onClick={onCollapse}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            data-testid="sidebar-collapse-btn"
            title="Colapsar menú"
          >
            <PanelLeftClose className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        <div className="px-3 mb-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 px-3">Principal</span>
        </div>

        {/* Inicio */}
        {isMenuVisible('inicio') && (
          <Link
            to="/inicio"
            onClick={() => { setOpenMenu(null); onItemClick(); }}
            className={cn('sidebar-link', currentPath === '/inicio' && 'active')}
            data-testid="nav-inicio"
          >
            <Home className="w-5 h-5 flex-shrink-0" />
            <span className="truncate">Inicio</span>
          </Link>
        )}

        {/* KPIs */}
        {kpisSubItems.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => toggleMenu('kpis')}
              className={cn('sidebar-link w-full', isInKPIsSection && 'active')}
              data-testid="nav-kpis-menu"
            >
              <Target className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1 text-left truncate">KPIs</span>
              {openMenu === 'kpis' ? <ChevronDown className="w-4 h-4 flex-shrink-0 transition-transform duration-200" /> : <ChevronRight className="w-4 h-4 flex-shrink-0 transition-transform duration-200" />}
            </button>
            <AnimatedSubmenu isOpen={openMenu === 'kpis'}>
              {kpisSubItems.map((item) => (
                <Link
                  key={item.subKey}
                  to={item.path}
                  onClick={onItemClick}
                  className={cn('sidebar-link pl-6 py-2 text-sm', (currentPath === item.path || currentPath.startsWith(item.path + '/')) && 'active')}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                </Link>
              ))}
            </AnimatedSubmenu>
          </div>
        )}

        {/* Mesa de Ayuda */}
        {showHelpDesk && (
          <div>
            <button
              type="button"
              onClick={() => toggleMenu('helpdesk')}
              className={cn('sidebar-link w-full', isInHelpdeskSection && 'active')}
              data-testid="nav-helpdesk-menu"
            >
              <HelpCircle className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1 text-left truncate">Mesa de Ayuda</span>
              {isModuleBeta('helpdesk') && userRole === 'admin' && (
                <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-semibold bg-amber-500/20 text-amber-400 rounded whitespace-nowrap min-w-[38px] flex-shrink-0">BETA</span>
              )}
              {openMenu === 'helpdesk' ? <ChevronDown className="w-4 h-4 flex-shrink-0 transition-transform duration-200" /> : <ChevronRight className="w-4 h-4 flex-shrink-0 transition-transform duration-200" />}
            </button>
            <AnimatedSubmenu isOpen={openMenu === 'helpdesk'}>
              {helpdeskSubItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onItemClick}
                  className={cn('sidebar-link pl-6 py-2 text-sm', currentPath === item.path && 'active')}
                  data-testid={`nav-helpdesk-${item.subKey.replace('helpdesk_', '')}`}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                </Link>
              ))}
            </AnimatedSubmenu>
          </div>
        )}

        {/* Base de Conocimiento */}
        {((userRole === 'admin' && isMenuVisible('knowledge')) || (userRole !== 'admin' && granularAllowsTopLevel('base_de_conocimiento'))) && (
          <Link
            to="/knowledge"
            onClick={onItemClick}
            className={cn('sidebar-link', currentPath.startsWith('/knowledge') && 'active')}
            data-testid="nav-knowledge"
          >
            <HelpCircle className="w-5 h-5 flex-shrink-0" />
            <span className="truncate">Base de Conocimiento</span>
          </Link>
        )}

        {/* Capital Humano */}
        {capitalHumanoSubItems.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => toggleMenu('capital_humano')}
              className={cn('sidebar-link w-full', isInCapitalHumanoSection && 'active')}
              data-testid="nav-capital-humano-menu"
            >
              <Users className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1 text-left truncate">Capital Humano</span>
              {openMenu === 'capital_humano' ? <ChevronDown className="w-4 h-4 flex-shrink-0 transition-transform duration-200" /> : <ChevronRight className="w-4 h-4 flex-shrink-0 transition-transform duration-200" />}
            </button>
            <AnimatedSubmenu isOpen={openMenu === 'capital_humano'}>
              {capitalHumanoSubItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onItemClick}
                  className={cn('sidebar-link pl-6 py-2 text-sm', currentPath === item.path && 'active')}
                  data-testid={`nav-capital-humano-${item.sub}`}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                </Link>
              ))}
            </AnimatedSubmenu>
          </div>
        )}

        {/* Vigilancia */}
        {vigilanciaSubItems.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => toggleMenu('vigilancia')}
              className={cn('sidebar-link w-full', isInVigilanciaSection && 'active')}
              data-testid="nav-vigilancia-menu"
            >
              <ShieldCheck className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1 text-left truncate">Vigilancia</span>
              {openMenu === 'vigilancia' ? <ChevronDown className="w-4 h-4 flex-shrink-0 transition-transform duration-200" /> : <ChevronRight className="w-4 h-4 flex-shrink-0 transition-transform duration-200" />}
            </button>
            <AnimatedSubmenu isOpen={openMenu === 'vigilancia'}>
              {vigilanciaSubItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onItemClick}
                  className={cn('sidebar-link pl-6 py-2 text-sm', currentPath === item.path && 'active')}
                  data-testid={`nav-vigilancia-${item.sub}`}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                </Link>
              ))}
            </AnimatedSubmenu>
          </div>
        )}

        {/* Admin Section */}
        {userRole === 'admin' && showAdminSection && (
          <>
            <div className="px-3 mt-6 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 px-3">Administración</span>
            </div>
            <div>
              <button
                type="button"
                onClick={() => toggleMenu('users')}
                className={cn('sidebar-link w-full', isInUsersSection && 'active')}
                data-testid="nav-users-menu"
              >
                <Users className="w-5 h-5 flex-shrink-0" />
                <span className="flex-1 text-left truncate">Usuarios</span>
                {openMenu === 'users' ? <ChevronDown className="w-4 h-4 flex-shrink-0 transition-transform duration-200" /> : <ChevronRight className="w-4 h-4 flex-shrink-0 transition-transform duration-200" />}
              </button>
              <AnimatedSubmenu isOpen={openMenu === 'users'}>
                {usersSubItems.map((item) => (
                  <Link
                    key={item.subKey}
                    to={item.path}
                    onClick={onItemClick}
                    className={cn('sidebar-link pl-6 py-2 text-sm', (item.exact ? currentPath === item.path : currentPath.startsWith(item.path)) && 'active')}
                    data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
                  >
                    <item.icon className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </Link>
                ))}
              </AnimatedSubmenu>
            </div>
          </>
        )}

        {/* Settings Section */}
        {userRole === 'admin' && showSettingsSection && (
          <>
            <div className="px-3 mt-6 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 px-3">Configuración</span>
            </div>
            <div>
              <button
                type="button"
                onClick={() => toggleMenu('settings')}
                className={cn('sidebar-link w-full', isInSettingsSection && 'active')}
                data-testid="nav-settings-menu"
              >
                <Settings className="w-5 h-5 flex-shrink-0" />
                <span className="flex-1 text-left truncate">Ajustes</span>
                {openMenu === 'settings' ? <ChevronDown className="w-4 h-4 flex-shrink-0 transition-transform duration-200" /> : <ChevronRight className="w-4 h-4 flex-shrink-0 transition-transform duration-200" />}
              </button>
              <AnimatedSubmenu isOpen={openMenu === 'settings'}>
                {settingsSubItems.map((item) => (
                  <Link
                    key={item.subKey}
                    to={item.path}
                    onClick={onItemClick}
                    className={cn('sidebar-link pl-6 py-2 text-sm', currentPath === item.path && 'active')}
                    data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
                  >
                    <item.icon className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </Link>
                ))}
              </AnimatedSubmenu>
            </div>
          </>
        )}
      </nav>

      {/* User section */}
      <div className="sidebar-user px-4 py-3">
        <div 
          className="flex items-center gap-3 mb-3 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => setShowAvatarDialog(true)}
          data-testid="sidebar-user-profile"
        >
          <UserAvatar name={user?.name} avatarUrl={user?.avatar_url} size="md" />
          <div className="min-w-0">
            <p className="text-white font-medium truncate">{user?.name || 'Usuario'}</p>
            <p className="text-slate-400 text-xs truncate">
              {userRole === 'admin' ? 'Administrador' : userRole === 'viewer' ? 'Viewer' : 'Usuario'}
            </p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
          onClick={handleLogout}
          data-testid="btn-logout"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Cerrar sesión
        </Button>
      </div>
    </>
  );

  // Memoize mobile click handler
  const handleMobileItemClick = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  // Empty click handler for desktop
  const handleDesktopItemClick = useCallback(() => {}, []);

  return (
    <div className="app-layout">
      {/* Desktop Sidebar */}
      <aside
        className="app-sidebar hidden lg:flex"
        style={{
          width: sidebarCollapsed ? 0 : 256,
          opacity: sidebarCollapsed ? 0 : 1,
          transition: 'width 300ms ease, opacity 200ms ease',
          pointerEvents: sidebarCollapsed ? 'none' : 'auto',
        }}
        data-testid="app-sidebar"
      >
        {renderNavContent(handleDesktopItemClick, { onCollapse: toggleSidebarCollapse })}
      </aside>

      {/* Floating expand tab when sidebar is collapsed (desktop only) */}
      {sidebarCollapsed && (
        <button
          onClick={toggleSidebarCollapse}
          className="hidden lg:flex fixed left-0 top-1/2 -translate-y-1/2 z-50 w-6 h-14 items-center justify-center rounded-r-lg bg-slate-900 text-slate-400 hover:text-white hover:w-8 hover:bg-slate-800 shadow-lg transition-all duration-200"
          data-testid="sidebar-expand-btn"
          title="Expandir menú"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      )}

      {/* Mobile Header */}
      <div className="mobile-header lg:hidden">
        <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="text-slate-700" data-testid="mobile-menu-btn">
              <Menu className="w-6 h-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0 bg-slate-900 border-0">
            <div className="flex flex-col h-full">
              {renderNavContent(handleMobileItemClick, { isMobile: true })}
            </div>
          </SheetContent>
        </Sheet>
        
        <Link to="/inicio" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-slate-900 font-['Manrope']">Central Aretina</span>
        </Link>

        <div 
          className="cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => setShowAvatarDialog(true)}
          data-testid="header-user-avatar"
        >
          <UserAvatar name={user?.name} avatarUrl={user?.avatar_url} size="sm" />
        </div>
      </div>

      {/* Main content */}
      <main
        className="app-main"
        style={{
          marginLeft: undefined, // let CSS handle default
          transition: 'margin-left 300ms ease, max-width 300ms ease',
        }}
        data-collapsed={sidebarCollapsed || undefined}
      >
        <div className="p-4 sm:p-6 lg:p-8 w-full box-border max-w-full overflow-x-hidden">{children}</div>
      </main>

      {/* Avatar Upload Dialog */}
      <AvatarUploadDialog
        open={showAvatarDialog}
        onOpenChange={setShowAvatarDialog}
        user={user}
        onAvatarChanged={(url) => updateUser({ avatar_url: url })}
      />
    </div>
  );
};

export default Layout;
